import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import { createEmbedShare, revokeEmbedShare } from '@services/api/embedApiClient';
import {
  buildConnectionPublicFolderUrl,
  buildEmbedShareInfo,
  buildPublicDriveEmbedUrl,
} from '@utils/helpers/embedHelpers';
import type { EmbedShare, EmbedTargetType, EmbedViewType } from '@/types/embed.types';
import type { StorageScope } from '@services/api/apiConfig';

export interface EmbedTarget {
  targetType: EmbedTargetType;
  folderPath: string;
  folderName: string;
  filePath?: string;
  initialViewType?: EmbedViewType;
}

interface EmbedDialogProps {
  open: boolean;
  target: EmbedTarget | null;
  shares: EmbedShare[];
  onClose: () => void;
  onSharesChanged: () => Promise<void> | void;
  /**
   * Customer's R2 public domain (e.g., https://drive.opendoorpc.org).
   * When set, share URLs are built against this origin instead of the
   * Box admin origin. Recipients see the customer-branded URL.
   */
  publicUrl?: string;
  connectionPrefix?: string;
  storageScope: StorageScope | null;
}

const VIEW_LABELS: Record<EmbedViewType, string> = {
  gallery: 'Gallery',
  'pdf-library': 'Library',
  'pdf-reader': 'Reader',
};

function shareTargetType(share: EmbedShare): EmbedTargetType {
  return share.targetType === 'file' ? 'file' : 'folder';
}

function shareMatchesTarget(share: EmbedShare, target: EmbedTarget): boolean {
  if (shareTargetType(share) !== target.targetType) return false;
  if (target.targetType === 'file') return share.filePath === target.filePath;
  return share.folderPath === target.folderPath;
}

export function EmbedDialog({
  open,
  target,
  shares,
  onClose,
  onSharesChanged,
  publicUrl,
  connectionPrefix,
  storageScope,
}: EmbedDialogProps) {
  const [title, setTitle] = useState('');
  const [viewType, setViewType] = useState<EmbedViewType>('gallery');
  const [activeShareId, setActiveShareId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<'folder-url' | 'url' | 'snippet' | null>(null);
  const [ensuredShare, setEnsuredShare] = useState<EmbedShare | null>(null);

  const targetShares = useMemo(() => {
    if (!target) return [];
    return shares.filter((share) => shareMatchesTarget(share, target));
  }, [shares, target]);

  const availableShares = useMemo(() => {
    if (!target || !ensuredShare || !shareMatchesTarget(ensuredShare, target)) return targetShares;
    const existing = targetShares.find((share) => share.id === ensuredShare.id);
    if (existing && existing.viewType === ensuredShare.viewType) return targetShares;
    return [ensuredShare, ...targetShares.filter((share) => share.id !== ensuredShare.id)];
  }, [ensuredShare, target, targetShares]);

  const activeShare = useMemo(() => {
    if (!availableShares.length) return null;
    return availableShares.find((share) => share.id === activeShareId) || availableShares[0];
  }, [activeShareId, availableShares]);

  const publicFolderUrl = target?.targetType === 'folder'
    ? buildConnectionPublicFolderUrl(target.folderPath, {
        publicUrl,
        prefix: connectionPrefix,
      }) || ''
    : '';
  const boxEmbedUrl = activeShare
    ? buildPublicDriveEmbedUrl(activeShare, { forceBoxOrigin: true, view: viewType })
    : '';
  const primaryEmbedUrl = boxEmbedUrl;
  const iframeSnippet = activeShare
    ? buildEmbedShareInfo(activeShare, {
        forceBoxOrigin: true,
        view: viewType,
      }).snippet
    : '';

  useEffect(() => {
    if (!open || !target) return;
    setTitle(target.folderName);
    setViewType(target.initialViewType || (target.targetType === 'file' ? 'pdf-reader' : 'gallery'));
    setActiveShareId(null);
    setEnsuredShare(null);
    setError(null);
    setCopiedField(null);
  }, [open, target]);

  const ensureDefaultShare = useCallback(async () => {
    if (!target || !storageScope) return;
    setBusy(true);
    setError(null);
    setCopiedField(null);
    try {
      const share = await createEmbedShare({
        folderPath: target.folderPath,
        filePath: target.filePath,
        targetType: target.targetType,
        title: target.folderName,
        viewType,
      }, storageScope);
      setEnsuredShare(share);
      setActiveShareId(share.id);
      await onSharesChanged();
    } catch (err) {
      setError((err as Error)?.message || 'Failed to prepare share link');
    } finally {
      setBusy(false);
    }
  }, [onSharesChanged, storageScope, target, viewType]);

  useEffect(() => {
    if (!open || !target) return;
    const matching = targetShares.find((share) => share.viewType === viewType) || targetShares[0];
    setActiveShareId(matching?.id || null);
  }, [open, target, targetShares, viewType]);

  const handleRevoke = async (share: EmbedShare) => {
    setBusy(true);
    setError(null);
    setCopiedField(null);
    try {
      if (!storageScope) throw new Error('Select an assigned connection first');
      await revokeEmbedShare(share.id, storageScope);
      if (activeShareId === share.id) setActiveShareId(null);
      await onSharesChanged();
    } catch (err) {
      setError((err as Error)?.message || 'Failed to revoke share');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async (text: string, field: 'folder-url' | 'url' | 'snippet') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField((curr) => (curr === field ? null : curr)), 1500);
    } catch (err) {
      setError((err as Error)?.message || 'Clipboard is unavailable');
    }
  };

  const handleOpen = () => {
    if (!primaryEmbedUrl) return;
    window.open(primaryEmbedUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IosShareRoundedIcon fontSize="small" />
        Share
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {target && (
            <Typography sx={{ color: 'text.secondary', fontSize: 13, fontFamily: 'monospace' }}>
              /{target.targetType === 'file' ? target.filePath : target.folderPath}
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {publicFolderUrl && (
            <Stack spacing={1}>
              <Typography className="font-label" sx={{ fontSize: 10, color: 'text.secondary' }}>
                Public Folder Link (R2 custom domain)
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={publicFolderUrl}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <Tooltip title="Open the public folder in a new tab">
                  <IconButton
                    size="small"
                    onClick={() => window.open(publicFolderUrl, '_blank', 'noopener,noreferrer')}
                    aria-label="Open public folder"
                  >
                    <OpenInNewRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ContentCopyRoundedIcon />}
                  onClick={() => { void handleCopy(publicFolderUrl, 'folder-url'); }}
                >
                  {copiedField === 'folder-url' ? 'Copied' : 'Copy Link'}
                </Button>
              </Stack>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                Opens the folder listing directly. Anyone with access to this public R2 domain can browse it.
              </Typography>
            </Stack>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField label="Title" value={title} size="small" fullWidth InputProps={{ readOnly: true }} />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewType}
              onChange={(_event, nextValue: EmbedViewType | null) => {
                if (nextValue) setViewType(nextValue);
              }}
              aria-label="Embed view type"
            >
              {target?.targetType !== 'file' && (
                <ToggleButton value="gallery" aria-label="Gallery">
                  <Tooltip title="Image gallery">
                    <PhotoLibraryRoundedIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              )}
              <ToggleButton value="pdf-library" aria-label="PDF library">
                <Tooltip title="PDF library">
                  <PictureAsPdfRoundedIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="pdf-reader" aria-label="PDF reader">
                <Tooltip title="PDF reader">
                  <CodeRoundedIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {!activeShare && (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={busy || !storageScope}
                  onClick={() => { void ensureDefaultShare(); }}
                >
                  {busy ? 'Preparing…' : 'Create Share Link'}
                </Button>
              }
            >
              No public share exists yet. Opening this dialog does not publish the folder.
            </Alert>
          )}

          {availableShares.length > 0 && (
            <Box>
              <Typography className="font-label" sx={{ fontSize: 10, color: 'text.secondary', mb: 1 }}>
                Default Share Link
              </Typography>
              <Stack spacing={1}>
                {availableShares.map((share) => (
                  <Box
                    key={share.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      border: '1px solid rgba(143,245,255,0.12)',
                      borderRadius: 1,
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => setActiveShareId(share.id)}
                      sx={{ justifyContent: 'flex-start', flex: 1, minWidth: 0 }}
                    >
                      <Stack sx={{ minWidth: 0, alignItems: 'flex-start' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
                          {VIEW_LABELS[share.viewType]}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
                          {share.title}
                        </Typography>
                      </Stack>
                    </Button>
                    <Tooltip title="Revoke share">
                      <IconButton size="small" color="error" disabled={busy} onClick={() => { void handleRevoke(share); }}>
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {activeShare && (
            <>
              <Divider />

              {/* Share URL — primary action surface. Open button verifies the
                  share works in 1 click; Copy URL gives recipients the link
                  they actually need (most people don't want an iframe). */}
              <Stack spacing={1}>
                <Typography className="font-label" sx={{ fontSize: 10, color: 'text.secondary' }}>
                  Viewer Share Link
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    value={primaryEmbedUrl}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                  <Tooltip title="Open in new tab to verify it loads">
                    <IconButton size="small" onClick={handleOpen} aria-label="Open share">
                      <OpenInNewRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyRoundedIcon />}
                    onClick={() => { void handleCopy(primaryEmbedUrl, 'url'); }}
                  >
                    {copiedField === 'url' ? 'Copied' : 'Copy Link'}
                  </Button>
                </Stack>

              </Stack>

              {/* Iframe code — secondary, for embedding in another site */}
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography className="font-label" sx={{ fontSize: 10, color: 'text.secondary' }}>
                    Embed Code
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyRoundedIcon />}
                    onClick={() => { void handleCopy(iframeSnippet, 'snippet'); }}
                  >
                    {copiedField === 'snippet' ? 'Copied' : 'Copy Code'}
                  </Button>
                </Stack>
                <TextField
                  value={iframeSnippet}
                  multiline
                  minRows={4}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& textarea': {
                      fontFamily: 'monospace',
                      fontSize: 12,
                    },
                  }}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
